import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getCsrfToken } from "../services/authService";
import { useAuth } from "../hooks/useAuth";
import "../styles/Shop.css";

export default function Shop() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [sort, setSort] = useState("nameAsc");
  const [favorites, setFavorites] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const { authenticated } = useAuth();

  const [compatibilityBrand, setCompatibilityBrand] = useState("");
  const [compatibilityModel, setCompatibilityModel] = useState("");

  const location = useLocation();

  useEffect(() => {
    const saved = JSON.parse(
      localStorage.getItem("recentlyViewedProducts") || "[]"
    );

    setRecentlyViewed(saved);
  }, []);

  useEffect(() => {
    if (location.state?.search) {
      setSearch(location.state.search);
    }
  }, [location.state]);

  useEffect(() => {
    loadProducts();

    if (authenticated) {
      loadFavorites();
    }
  }, []);

  useEffect(() => {
    const pendingFavoriteProductId = sessionStorage.getItem(
      "pendingFavoriteProductId"
    );

    if (!authenticated || !pendingFavoriteProductId) return;

    sessionStorage.removeItem("pendingFavoriteProductId");

    toggleFavorite(
      { stopPropagation: () => {} },
      Number(pendingFavoriteProductId)
    );
  }, [authenticated]);

  const loadProducts = async () => {
    const response = await fetch("/api/products", {
      credentials: "include",
    });

    if (response.ok) {
      setProducts(await response.json());
    }
  };

  const availableBrands = useMemo(() => {
    return [...new Set(
      products.flatMap(
        (product) =>
          product.compatibilities?.map((c) => c.brandName) || []
      )
    )].sort();
  }, [products]);

  const availableModels = useMemo(() => {
    return products
      .flatMap((product) => product.compatibilities || [])
      .filter(
        (c) =>
          !compatibilityBrand ||
          c.brandName === compatibilityBrand
      )
      .filter(
        (c, index, array) =>
          array.findIndex(
            (x) => x.equipmentModelId === c.equipmentModelId
          ) === index
      )
      .sort((a, b) => a.modelName.localeCompare(b.modelName));
  }, [products, compatibilityBrand]);

  const filteredProducts = useMemo(() => {
    return products
      .filter((product) =>
        `${product.name ?? ""}`
          .toLowerCase()
          .includes(search.toLowerCase())
      )
      .filter((product) => {
        if (stockFilter === "inStock") return product.stock > 0;
        if (stockFilter === "outOfStock") return product.stock <= 0;
        return true;
      })
      .filter((product) => {
        if (!compatibilityBrand && !compatibilityModel) return true;

        return product.compatibilities?.some((compatibility) => {
          const matchesBrand =
            !compatibilityBrand || compatibility.brandName === compatibilityBrand;

          const matchesModel =
            !compatibilityModel ||
            Number(compatibility.equipmentModelId) === Number(compatibilityModel);

          return matchesBrand && matchesModel;
        });
      })
      .sort((a, b) => {
        if (sort === "priceAsc") return Number(a.price) - Number(b.price);
        if (sort === "priceDesc") return Number(b.price) - Number(a.price);
        if (sort === "nameDesc") return b.name.localeCompare(a.name);

        if (sort === "topSelling") {
          return Number(b.salesCount || 0) - Number(a.salesCount || 0);
        }

        return a.name.localeCompare(b.name);
      });
  }, [products, search, stockFilter, sort, compatibilityBrand, compatibilityModel]);

  const loadFavorites = async () => {
    const response = await fetch("/api/favorites", {
      credentials: "include",
    });

    if (response.ok) {
      setFavorites(await response.json());
    }
  };

  const isFavorite = (productId) => {
    return favorites.some((favorite) => favorite.productId === productId);
  };

  const toggleFavorite = async (e, productId) => {
    e.stopPropagation();

    if (!authenticated) {
      sessionStorage.setItem(
        "pendingFavoriteProductId",
        String(productId)
      );

      navigate("/login", { state: { redirect: "/shop" } });
      return;
    }

    const csrf = await getCsrfToken();
    const favorite = isFavorite(productId);

    const response = await fetch(`/api/favorites/${productId}`, {
      method: favorite ? "DELETE" : "POST",
      credentials: "include",
      headers: {
        [csrf.headerName]: csrf.token,
      },
    });

    if (response.ok) {
      await loadFavorites();
    }
  };

  return (
    <main className="shop-page">
      <section className="shop-container">
        <div className="shop-header">
          <h1>{t("shop.title")}</h1>
          <p>{t("shop.subtitle")}</p>
        </div>

        <div className="shop-layout">
          <aside className="shop-filters">
            <h2>{t("shop.filters")}</h2>

            <label>
              {t("shop.search")}
              <input
                type="text"
                placeholder={t("shop.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>

            <label>
              {t("shop.stock")}
              <select
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
              >
                <option value="all">{t("shop.all")}</option>
                <option value="inStock">{t("shop.inStock")}</option>
                <option value="outOfStock">{t("shop.outOfStock")}</option>
              </select>
            </label>

            <label>
              {t("shop.sortBy")}
              <select value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="nameAsc">{t("shop.nameAsc")}</option>
                <option value="nameDesc">{t("shop.nameDesc")}</option>
                <option value="priceAsc">{t("shop.priceAsc")}</option>
                <option value="priceDesc">{t("shop.priceDesc")}</option>
                <option value="topSelling">{t("shop.topSelling")}</option>
              </select>
            </label>

            <label>
              {t("shop.compatibilityBrand")}

              <select
                value={compatibilityBrand}
                onChange={(e) => {
                  setCompatibilityBrand(e.target.value);
                  setCompatibilityModel("");
                }}
              >
                <option value="">
                  {t("shop.allBrands")}
                </option>

                {availableBrands.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
            </label>

            {compatibilityBrand && (
              <label>
                {t("shop.compatibilityModel")}

                <select
                  value={compatibilityModel}
                  onChange={(e) =>
                    setCompatibilityModel(e.target.value)
                  }
                >
                  <option value="">
                    {t("shop.allModels")}
                  </option>

                  {availableModels.map((model) => (
                    <option
                      key={model.equipmentModelId}
                      value={model.equipmentModelId}
                    >
                      {model.brandName} {model.modelName}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <button
              type="button"
              className="shop-clear-filters"
              onClick={() => {
                setSearch("");
                setStockFilter("all");
                setSort("nameAsc");
                setCompatibilityBrand("");
                setCompatibilityModel("");
              }}
            >
              {t("shop.clearFilters")}
            </button>
          </aside>

          <section className="shop-results">
            <div className="shop-results-top">
              <span>
                {filteredProducts.length} {t("shop.results")}
              </span>
            </div>

            <div className="shop-grid">
              {filteredProducts.length === 0 ? (
                <p className="shop-empty">{t("shop.empty")}</p>
              ) : (
                filteredProducts.map((product) => (
                  <article key={product.id} className="shop-card">
                    <div
                      className="shop-image-wrapper"
                      onClick={() => navigate(`/shop/${product.slug}`)}
                    >
                      <button
                        type="button"
                        className={`shop-favorite-btn ${isFavorite(product.id) ? "active" : ""}`}
                        onClick={(e) => toggleFavorite(e, product.id)}
                      >
                        ♥
                      </button>
                      
                      {product.imageUrl ? (
                        <img src={product.imageUrl} alt={product.name} />
                      ) : (
                        <div className="shop-image-placeholder">
                          {t("shop.noImage")}
                        </div>
                      )}
                    </div>

                    <div className="shop-card-content">
                      <h2 onClick={() => navigate(`/shop/${product.slug}`)}>
                        {product.name}
                      </h2>

                      <div className="shop-rating">
                        <span>
                          {"★".repeat(Math.round(product.averageRating || 0))}
                          {"☆".repeat(5 - Math.round(product.averageRating || 0))}
                        </span>

                        <small>
                          {Number(product.averageRating || 0).toFixed(1)} ·{" "}
                          {product.reviewsCount || 0} {t("shop.reviews")}
                        </small>
                      </div>

                      {/* {Number(product.salesCount || 0) > 0 && (
                        <p className="shop-sales-count">
                          {product.salesCount} {t("shop.sales")}
                        </p>
                      )} */}

                      <div className="shop-card-footer">
                        <strong>{Number(product.price).toFixed(2)} €</strong>
                        <span className={product.stock > 0 ? "stock-ok" : "stock-empty"}>
                          {product.stock > 0
                            ? t("shop.inStock")
                            : t("shop.outOfStock")}
                        </span>

                        {product.stock > 0 && product.stock <= 5 && (
                          <p className="stock-warning">
                            {t("shop.lowStock")}
                          </p>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => navigate(`/shop/${product.slug}`)}
                      >
                        {t("shop.viewProduct")}
                      </button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </section>
        </div>

        {recentlyViewed.length > 0 && (
          <section className="recently-viewed-section">
            <h2>{t("shop.recentlyViewed")}</h2>

            <div className="recently-viewed-grid">
              {recentlyViewed.map((product) => (
                <article
                  key={product.id}
                  className="recently-viewed-card"
                  onClick={() => navigate(`/shop/${product.slug}`)}
                >
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                    />
                  ) : (
                    <div className="shop-image-placeholder">
                      {t("shop.noImage")}
                    </div>
                  )}

                  <h3>{product.name}</h3>

                  <div className="shop-rating">
                    <span>
                      {"★".repeat(
                        Math.round(product.averageRating || 0)
                      )}
                      {"☆".repeat(
                        5 - Math.round(product.averageRating || 0)
                      )}
                    </span>

                    <small>
                      {Number(product.averageRating || 0).toFixed(1)}
                    </small>
                  </div>

                  <strong>
                    {Number(product.price).toFixed(2)} €
                  </strong>
                </article>
              ))}
            </div>
          </section>
        )}
      </section>
    </main>
  );
}